import React from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Download, Loader2, PackageSearch } from 'lucide-react';

import { useViewModelReady } from '../../../../hooks/useViewModel';
import type {
  NexusBugSummary,
  NexusDeliveryItem,
  NexusExportScope,
  NexusLedgerDelivery,
  NexusOutcomeCounts,
} from '../../../../domain/nexus/model';
import { CopyListButton, CopyOnClick } from './CopyOnClick';
import {
  bugEditorSearch,
  bugLabel,
  copyableTaskId,
  deliveryLabel,
  deliveryTypeLabel,
  drillDownSearch,
  outcomeLabel,
  outcomeTone,
  projectIdsOf,
  shortVersion,
  sourceKindLabel,
  sourceLocation,
  taskStateLabel,
  validationSummary,
} from './deliveredTaskHelpers';
import {
  useBugDetailViewModel,
  useCsvExport,
  useDeliveryOverviewViewModel,
  useProjectDetailViewModel,
  type DrillDownViewModel,
} from './deliveryBrowserViewModels';
import { NexusErrorBanner } from './ErrorBanner';
import { formatNexusTimestamp } from './helpers';
import { NexusStatusBadge } from './StatusBadge';

const number = (value: number | null | undefined) => (value ?? 0).toLocaleString();
const NUMERIC_COLUMNS = new Set(['Nexus projects', 'Tasks', 'Delivered', 'Awaiting', 'Not accepted']);

/** Bug → project → task drill-down. The URL (`?bug=&project=`) selects the level. */
export function DeliveryBrowser({ bug, project }: { bug: string | null; project: string | null }) {
  if (bug && project) return <ProjectView key={`${bug}/${project}`} bug={bug} projectId={project} />;
  if (bug) return <BugView key={bug} bug={bug} />;
  return <OverviewView />;
}

function Breadcrumbs({ bug, project }: { bug?: string; project?: string }) {
  const crumbs: { label: string; search: string | null }[] = [
    { label: 'All bugs', search: bug ? drillDownSearch() : null },
    ...(bug ? [{ label: `Bug ${bug}`, search: project ? drillDownSearch({ bug }) : null }] : []),
    ...(project ? [{ label: project, search: null }] : []),
  ];
  return (
    <nav aria-label="Delivered tasks" className="mt-4 flex flex-wrap items-center gap-1 text-sm">
      {crumbs.map((crumb, index) => (
        <React.Fragment key={crumb.label}>
          {index > 0 && <ChevronRight className="h-4 w-4 text-gray-400" aria-hidden="true" />}
          {crumb.search === null ? (
            <span className="font-medium text-gray-900" aria-current="page">{crumb.label}</span>
          ) : (
            <Link to={{ search: crumb.search }} className="text-primary hover:underline">{crumb.label}</Link>
          )}
        </React.Fragment>
      ))}
    </nav>
  );
}

function ExportButton({ scope, id, label }: { scope: NexusExportScope; id?: string; label: string }) {
  const csv = useCsvExport(scope, id);
  return (
    <>
      <button
        type="button"
        onClick={() => void csv.exportCsv()}
        disabled={csv.exporting}
        className="inline-flex h-9 items-center gap-2 rounded-md border border-gray-300 bg-white px-3 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
      >
        {csv.exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
        {label}
      </button>
      {csv.error && <div className="basis-full"><NexusErrorBanner message={csv.error} onDismiss={csv.clearError} /></div>}
    </>
  );
}

function LevelHeader({ title, detail, children }: { title: React.ReactNode; detail?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="mt-3 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
      <div className="min-w-0">
        <h2 className="text-lg font-semibold text-gray-900">{title}</h2>
        {detail && <div className="mt-0.5 text-sm text-gray-500">{detail}</div>}
      </div>
      <div className="flex flex-wrap items-center gap-2">{children}</div>
    </div>
  );
}

function CountsLine({ counts, total }: { counts: Partial<NexusOutcomeCounts> | null; total: number }) {
  return (
    <span>
      {number(total)} task{total === 1 ? '' : 's'} · <span className="text-emerald-700">{number(counts?.ingested)} delivered</span>
      {Boolean(counts?.submitted) && <> · <span className="text-amber-700">{number(counts?.submitted)} awaiting results</span></>}
      {Boolean(counts?.unknown) && <> · <span className="text-amber-700">{number(counts?.unknown)} outcome unknown</span></>}
      {Boolean(counts?.rejected) && <> · <span className="text-red-700">{number(counts?.rejected)} not accepted</span></>}
    </span>
  );
}

function LevelState<T>({ vm, empty, children }: { vm: DrillDownViewModel<T>; empty: boolean; children: React.ReactNode }) {
  if (vm.busy) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }
  if (vm.error) return <NexusErrorBanner message={vm.error} onDismiss={vm.clearError} />;
  if (empty) {
    return (
      <div className="mt-4 flex flex-col items-center rounded-md border border-gray-200 bg-white px-5 py-10 text-center text-gray-400">
        <PackageSearch className="h-6 w-6" />
        <p className="mt-2 text-sm font-medium text-gray-700">No delivered tasks recorded here yet</p>
      </div>
    );
  }
  return <>{children}</>;
}

function Table({ head, children }: { head: string[]; children: React.ReactNode }) {
  return (
    <section className="mt-4 overflow-hidden rounded-md border border-gray-200 bg-white">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-left text-sm">
          <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
            <tr>
              {head.map((label, index) => (
                <th key={label} className={`px-3 py-3 font-medium ${NUMERIC_COLUMNS.has(label) ? 'text-right' : ''} ${index === 0 ? 'sm:pl-5' : ''} ${index === head.length - 1 ? 'sm:pr-5' : ''}`}>{label}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">{children}</tbody>
        </table>
      </div>
    </section>
  );
}

function OutcomeCells({ counts }: { counts: Partial<NexusOutcomeCounts> | null }) {
  const awaiting = (counts?.submitted ?? 0) + (counts?.unknown ?? 0);
  return (
    <>
      <td className="px-3 py-3 text-right font-mono text-xs text-emerald-700">{number(counts?.ingested)}</td>
      <td className={`px-3 py-3 text-right font-mono text-xs ${awaiting ? 'text-amber-700' : 'text-gray-400'}`}>{number(awaiting)}</td>
      <td className={`px-3 py-3 text-right font-mono text-xs ${counts?.rejected ? 'text-red-700' : 'text-gray-400'}`}>{number(counts?.rejected)}</td>
    </>
  );
}

// --- Overview: one row per bug ----------------------------------------------

function OverviewView() {
  const vm = useDeliveryOverviewViewModel();
  useViewModelReady(vm.initialize);
  const bugs = vm.data?.bugs ?? [];
  const totals = bugs.reduce((sum, bug) => ({
    taskCount: sum.taskCount + bug.taskCount,
    counts: {
      ingested: sum.counts.ingested + bug.counts.ingested,
      submitted: sum.counts.submitted + bug.counts.submitted,
      unknown: sum.counts.unknown + bug.counts.unknown,
      rejected: sum.counts.rejected + bug.counts.rejected,
    },
  }), { taskCount: 0, counts: { ingested: 0, submitted: 0, unknown: 0, rejected: 0 } });
  return (
    <>
      <Breadcrumbs />
      <LevelHeader
        title="Delivered tasks by bug"
        detail={vm.data && <>{number(bugs.length)} bug{bugs.length === 1 ? '' : 's'} · <CountsLine counts={totals.counts} total={totals.taskCount} /></>}
      >
        <ExportButton scope="all" label="Download CSV" />
      </LevelHeader>
      <LevelState vm={vm} empty={!bugs.length}>
        <Table head={['Bug', 'Nexus projects', 'Tasks', 'Delivered', 'Awaiting', 'Not accepted', 'Last delivery']}>
          {bugs.map((bug) => <BugRow key={bug.deliveryBugId} bug={bug} />)}
        </Table>
      </LevelState>
    </>
  );
}

function BugRow({ bug }: { bug: NexusBugSummary }) {
  return (
    <tr className="align-top hover:bg-gray-50">
      <td className="px-3 py-3 sm:pl-5">
        <Link to={{ search: drillDownSearch({ bug: bug.deliveryBugId }) }} className="font-mono text-sm font-medium text-primary hover:underline">
          {bug.deliveryBugId}
        </Link>
        {bugLabel(bug) && <p className="mt-0.5 max-w-xs text-[11px] text-gray-500">{bugLabel(bug)}</p>}
      </td>
      <td className="px-3 py-3 text-right font-mono text-xs text-gray-900">
        {number(bug.projectCount)}
        {bug.attemptCount > 0 && <p className="mt-0.5 font-sans text-[11px] text-amber-700">+{bug.attemptCount} unknown</p>}
      </td>
      <td className="px-3 py-3 text-right font-mono text-xs text-gray-900">{number(bug.taskCount)}</td>
      <OutcomeCells counts={bug.counts} />
      <td className="whitespace-nowrap px-3 py-3 text-xs text-gray-500 sm:pr-5">{formatNexusTimestamp(bug.lastDeliveredAt)}</td>
    </tr>
  );
}

// --- Bug: its Nexus projects ----------------------------------------------

function BugView({ bug }: { bug: string }) {
  const vm = useBugDetailViewModel(bug);
  useViewModelReady(vm.initialize);
  const projects = vm.data?.projects ?? [];
  const projectIds = projectIdsOf(projects);
  return (
    <>
      <Breadcrumbs bug={bug} />
      <LevelHeader
        title={<>Bug <span className="font-mono">{bug}</span></>}
        detail={vm.data && (
          <>
            <p>
              {bugLabel(vm.data.bug) || 'No project details yet'}
              {' · '}
              <Link to={{ search: bugEditorSearch(bug) }} className="text-primary hover:underline">
                {bugLabel(vm.data.bug) ? 'Edit details' : 'Add details'}
              </Link>
            </p>
            {vm.data.bug.description && <p className="mt-0.5 max-w-3xl whitespace-pre-line text-xs">{vm.data.bug.description}</p>}
            <p>{number(projectIds.length)} Nexus project{projectIds.length === 1 ? '' : 's'} · <CountsLine counts={vm.data.counts} total={vm.data.taskCount} /></p>
          </>
        )}
      >
        <ExportButton scope="bug" id={bug} label="Download CSV" />
        <CopyListButton ids={projectIds} noun="project ID" />
        <CopyListButton ids={vm.data?.deliveredTaskIds ?? []} noun="task ID" />
      </LevelHeader>
      <LevelState vm={vm} empty={!projects.length}>
        <Table head={['Nexus project', 'Delivery', 'Status', 'Tasks', 'Delivered', 'Awaiting', 'Not accepted', 'Source', 'Submitted']}>
          {projects.map((delivery) => <ProjectRow key={delivery.deliveryId} bug={bug} delivery={delivery} />)}
        </Table>
      </LevelState>
    </>
  );
}

function ProjectRow({ bug, delivery }: { bug: string; delivery: NexusLedgerDelivery }) {
  const source = delivery.source?.uri || null;
  return (
    <tr className="align-top hover:bg-gray-50">
      <td className="px-3 py-3 sm:pl-5">
        <Link
          to={{ search: drillDownSearch({ bug, project: delivery.deliveryId }) }}
          className={`whitespace-nowrap text-sm font-medium text-primary hover:underline ${delivery.remoteProjectId ? 'font-mono' : ''}`}
        >
          {deliveryLabel(delivery)}
        </Link>
        {!delivery.remoteProjectId && <p className="mt-0.5 break-all font-mono text-[11px] text-gray-500">{delivery.deliveryId}</p>}
        {delivery.testRun && <p className="mt-0.5 text-[11px] text-blue-600">test run</p>}
      </td>
      <td className="px-3 py-3 text-xs">
        <p className="text-gray-900">{delivery.deliveryName ?? '—'}</p>
        <p className="mt-0.5 text-[11px] text-gray-500">{deliveryTypeLabel(delivery.deliveryType)}</p>
      </td>
      <td className="px-3 py-3">
        <NexusStatusBadge status={delivery.projectState || 'UNKNOWN'} />
        {delivery.lastSyncError && (
          <p className="mt-1 max-w-[12rem] text-[11px] text-amber-700" title={delivery.lastSyncError}>Last check failed</p>
        )}
      </td>
      <td className="px-3 py-3 text-right font-mono text-xs text-gray-900">{number(delivery.taskCount)}</td>
      <OutcomeCells counts={delivery.outcomeCounts} />
      <td className="max-w-[14rem] px-3 py-3 text-xs">
        <p className="text-[11px] text-gray-500">{sourceKindLabel(delivery.source?.kind)}</p>
        {source && (
          <CopyOnClick value={source} label="source location" className="-mx-1 mt-0.5 px-1">
            <span className="block truncate font-mono text-[11px] text-gray-500">{source}</span>
          </CopyOnClick>
        )}
      </td>
      <td className="whitespace-nowrap px-3 py-3 text-xs text-gray-500 sm:pr-5">{formatNexusTimestamp(delivery.submittedAt)}</td>
    </tr>
  );
}

// --- Project: every task sent in it ----------------------------------------

function ProjectView({ bug, projectId }: { bug: string; projectId: string }) {
  const vm = useProjectDetailViewModel(projectId);
  useViewModelReady(vm.initialize);
  const project = vm.data?.project ?? null;
  const tasks = vm.data?.tasks ?? [];
  const counts = tasks.reduce((sum, task) => (task.outcome ? { ...sum, [task.outcome]: sum[task.outcome] + 1 } : sum),
    { ingested: 0, submitted: 0, unknown: 0, rejected: 0 } as NexusOutcomeCounts);
  return (
    <>
      <Breadcrumbs bug={bug} project={projectId} />
      <LevelHeader
        title={<span className="font-mono">{project ? deliveryLabel(project) : projectId}</span>}
        detail={project && (
          <>
            <p>
              {project.deliveryName ?? '—'} · {deliveryTypeLabel(project.deliveryType)} · <NexusStatusBadge status={project.projectState || 'UNKNOWN'} />
            </p>
            <p><CountsLine counts={counts} total={tasks.length} /></p>
            {project.changeReason && <p className="mt-0.5 text-xs">Change reason: {project.changeReason}</p>}
          </>
        )}
      >
        <ExportButton scope="project" id={projectId} label="Download CSV" />
        <CopyListButton ids={vm.data?.deliveredTaskIds ?? []} noun="task ID" />
      </LevelHeader>
      <LevelState vm={vm} empty={!tasks.length}>
        <Table head={['Task', 'Result', 'Source', 'Replaces']}>
          {tasks.map((task) => <ItemRow key={task.taskId} task={task} />)}
        </Table>
      </LevelState>
    </>
  );
}

function ItemRow({ task }: { task: NexusDeliveryItem }) {
  const location = sourceLocation(task);
  const state = taskStateLabel(task.taskState);
  const validation = validationSummary(task.validationStates);
  return (
    <tr className="align-top">
      <td className="min-w-[10rem] px-3 py-3 sm:pl-5">
        <CopyOnClick value={copyableTaskId(task)} label="task ID" className="-mx-1 px-1">
          <span className="block break-all font-mono text-xs font-medium text-gray-900">{task.taskId}</span>
          {task.version && <span className="mt-0.5 block font-mono text-[11px] text-gray-500">@{shortVersion(task.version)}</span>}
        </CopyOnClick>
      </td>
      <td className="px-3 py-3">
        <span className={`inline-flex whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ${outcomeTone(task.outcome)}`}>
          {outcomeLabel(task.outcome)}
        </span>
        {state && <p className="mt-1 whitespace-nowrap text-[11px] text-gray-500">{state}</p>}
        {validation && <p className="mt-0.5 whitespace-nowrap text-[11px] text-gray-500">{validation}</p>}
        {task.resultComment && <p className="mt-0.5 max-w-[16rem] text-[11px] text-gray-500">{task.resultComment}</p>}
      </td>
      <td className="max-w-[18rem] px-3 py-3 text-xs">
        {task.sourceTaskId && task.sourceTaskId !== task.taskId && <p className="break-all font-mono text-gray-900">{task.sourceTaskId}</p>}
        {task.synthPipelineId && task.synthPipelineId !== task.sourceTaskId && (
          <p className="mt-0.5 break-all font-mono text-[11px] text-gray-500">{task.synthPipelineId}</p>
        )}
        <p className="mt-0.5 text-[11px] text-gray-500">{sourceKindLabel(task.sourceKind)}</p>
        {location && (
          <CopyOnClick value={location} label="source location" className="-mx-1 mt-0.5 px-1">
            <span className="block truncate font-mono text-[11px] text-gray-500">{location}</span>
          </CopyOnClick>
        )}
      </td>
      <td className="px-3 py-3 text-xs sm:pr-5">
        {task.previousVersionedTaskId ? (
          <span className="break-all font-mono text-[11px] text-gray-500">{task.previousVersionedTaskId}</span>
        ) : (
          <span className="text-gray-400">—</span>
        )}
      </td>
    </tr>
  );
}
